import React from 'react';

// Spinner component for buttons and inline loading
export const Spinner: React.FC<{ size?: 'sm' | 'md' | 'lg'; className?: string }> = ({ 
  size = 'md', 
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'h-3 w-3 border',
    md: 'h-4 w-4 border-2',
    lg: 'h-8 w-8 border-2'
  };

  return (
    <div 
      role="status"
      aria-label="Loading"
      className={`animate-spin rounded-full border-white border-t-transparent ${sizeClasses[size]} ${className}`}
    />
  );
};

// Loading button content with spinner
export const LoadingButtonContent: React.FC<{ 
  isLoading: boolean; 
  loadingText: string; 
  defaultText: string;
  spinnerSize?: 'sm' | 'md' | 'lg';
}> = ({ isLoading, loadingText, defaultText, spinnerSize = 'md' }) => {
  if (isLoading) {
    return (
      <>
        <Spinner size={spinnerSize} className="mr-2" />
        {loadingText}
      </>
    );
  }
  return <>{defaultText}</>;
};

// ProGate skeleton loader
export const ProGateSkeleton: React.FC = () => (
  <div className="rounded-2xl border border-white/10 p-6 bg-white/5 backdrop-blur">
    <div className="animate-pulse">
      {/* Lock icon skeleton */}
      <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-white/10"></div>
      
      {/* Title skeleton */}
      <div className="h-6 bg-white/10 rounded-lg mb-2 mx-auto w-32"></div>
      
      {/* Benefits skeleton */}
      <div className="space-y-2 mb-6">
        <div className="h-4 bg-white/10 rounded w-48 mx-auto"></div>
        <div className="h-4 bg-white/10 rounded w-44 mx-auto"></div>
        <div className="h-4 bg-white/10 rounded w-52 mx-auto"></div>
      </div>
      
      {/* Button skeleton */}
      <div className="h-12 bg-white/10 rounded-xl mb-3"></div>
      
      {/* Trust indicator skeleton */}
      <div className="h-3 bg-white/10 rounded w-24 mx-auto"></div>
    </div>
  </div>
);

// Page loading skeleton
export const PageSkeleton: React.FC<{ 
  showHeader?: boolean; 
  showCard?: boolean; 
  showNavigation?: boolean;
}> = ({ 
  showHeader = true, 
  showCard = true, 
  showNavigation = true 
}) => (
  <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
    <div className="animate-pulse">
      {/* Header skeleton */}
      {showHeader && (
        <div className="text-center mb-12">
          <div className="h-10 bg-white/10 rounded-lg mb-4 mx-auto w-64"></div>
          <div className="h-6 bg-white/10 rounded-lg mx-auto w-96"></div>
        </div>
      )}

      {/* Main content card skeleton */}
      {showCard && (
        <div className="rounded-2xl shadow-sm border border-white/10 bg-white/5 backdrop-blur p-8 mb-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left side skeleton */}
            <div>
              <div className="h-8 bg-white/10 rounded-lg mb-6 w-48"></div>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start">
                    <div className="w-6 h-6 rounded-full bg-white/10 mr-3 mt-0.5"></div>
                    <div className="flex-1">
                      <div className="h-5 bg-white/10 rounded w-40 mb-1"></div>
                      <div className="h-4 bg-white/10 rounded w-56"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right side skeleton */}
            <div className="text-center">
              <div className="rounded-2xl border border-white/10 p-6 bg-white/5 backdrop-blur mb-6">
                <div className="h-12 bg-white/10 rounded-lg mb-2 w-24 mx-auto"></div>
                <div className="h-4 bg-white/10 rounded-lg mb-6 w-40 mx-auto"></div>
                <div className="h-12 bg-white/10 rounded-xl mb-3"></div>
                <div className="h-3 bg-white/10 rounded w-32 mx-auto"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Navigation skeleton */}
      {showNavigation && (
        <div className="text-center">
          <div className="h-10 bg-white/10 rounded-xl w-40 mx-auto"></div>
        </div>
      )}
    </div>
  </div>
);

// Success page skeleton
export const SuccessPageSkeleton: React.FC = () => (
  <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10">
    <div className="text-center animate-pulse">
      <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-white/10"></div>
      <div className="h-10 bg-white/10 rounded-lg mb-4 mx-auto w-64"></div>
      <div className="h-6 bg-white/10 rounded-lg mb-8 mx-auto w-96"></div>
      <div className="space-y-4">
        <div className="h-12 bg-white/10 rounded-xl mx-auto w-48"></div>
        <div className="h-10 bg-white/10 rounded-xl mx-auto w-40"></div>
      </div>
    </div>
  </div>
);

// Card loading skeleton
export const Card: React.FC = () => (
  <div className="bg-white dark:bg-dark-card rounded-lg p-6 border border-border shadow-[0_2px_8px_rgba(0,0,0,0.08)] dark:shadow-sm">
    <div className="animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
            <div className="h-5 bg-green-200 dark:bg-green-800 rounded-full w-16"></div>
          </div>
          <div className="flex items-center gap-4">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
          </div>
        </div>
        <div className="ml-6">
          <div className="h-10 bg-blue-200 dark:bg-blue-800 rounded-lg w-24"></div>
        </div>
      </div>
    </div>
  </div>
);

// Synchronization loading state
export const SyncLoadingState: React.FC<{ 
  isRefreshing: boolean; 
  title?: string; 
  description?: string;
}> = ({ 
  isRefreshing, 
  title = "Synchronizuji nákup…", 
  description = "Zpracováváme tvou platbu a aktivujeme Pro přístup. Chvilku strpení." 
}) => (
  <div className="text-center">
    <div className="mx-auto mb-6 w-16 h-16 rounded-full bg-blue-600/20 flex items-center justify-center">
      {isRefreshing ? (
        <Spinner size="lg" className="border-blue-400" />
      ) : (
        <div className="animate-pulse">
          <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
      )}
    </div>
    
    <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
      {title}
    </h1>
    
    <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
      {description}
    </p>
  </div>
);

// Default export object with all loading components
const LoadingComponents = {
  Spinner,
  LoadingButtonContent,
  ProGateSkeleton,
  PageSkeleton,
  SuccessPageSkeleton,
  Card,
  SyncLoadingState
};

export default LoadingComponents;